import React, { useEffect, useState } from 'react';
import API from '../utils/api';
import Confetti from './Confetti';
import { trackAchievementUnlocked } from '../utils/analytics';
import safeStorage from '../utils/safeStorage';

// Collectible achievement badges for the dashboard. Earned badges are
// colorful; locked ones are greyed with a hint (and progress where it
// applies), so the next badge always feels within reach. Newly earned
// badges (vs. what this browser has already seen) get confetti + a banner.
const SEEN_KEY = 'db_achievements_seen';

const AchievementsCard = () => {
  const [data, setData] = useState(null);
  const [newBadges, setNewBadges] = useState([]);
  const [celebrate, setCelebrate] = useState(false);

  useEffect(() => {
    API.get('/users/achievements')
      .then(res => {
        setData(res.data);

        const earnedIds = res.data.badges.filter(b => b.earned).map(b => b.id);
        let seen = [];
        try { seen = JSON.parse(safeStorage.getItem(SEEN_KEY) || '[]'); } catch { /* corrupt → treat as none */ }

        // First visit ever: record without celebrating a backlog
        if (!safeStorage.getItem(SEEN_KEY)) {
          safeStorage.setItem(SEEN_KEY, JSON.stringify(earnedIds));
          return;
        }

        const fresh = res.data.badges.filter(b => b.earned && !seen.includes(b.id));
        if (fresh.length > 0) {
          safeStorage.setItem(SEEN_KEY, JSON.stringify(earnedIds));
          setNewBadges(fresh);
          setCelebrate(true);
          fresh.forEach(b => trackAchievementUnlocked(b.id));
          setTimeout(() => setCelebrate(false), 5000);
        }
      })
      .catch(() => {}); // card simply doesn't render
  }, []);

  if (!data) return null;
  const { badges, earnedCount, total } = data;

  return (
    <>
      {celebrate && <Confetti />}
      <div className="rounded-4 p-4 mb-4" style={{background:'var(--card-bg)', border:'1px solid var(--border-purple)'}}>

        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="fw-bold" style={{color:'var(--text-main)', fontSize:'17px', fontFamily:"'Fredoka One',cursive"}}>
            🏅 Achievements
          </div>
          <span className="badge rounded-pill" style={{background:'var(--badge-purple-bg)', color:'var(--text-main)', fontSize:'12px', padding:'6px 12px'}}>
            {earnedCount} / {total} unlocked
          </span>
        </div>

        {newBadges.length > 0 && (
          <div className="text-center p-3 rounded-3 mb-3" style={{background:'var(--yellow-section)', border:'1px solid var(--border-yellow)'}}>
            <strong style={{color:'var(--text-main)', fontSize:'15px'}}>
              🎉 New badge{newBadges.length > 1 ? 's' : ''} unlocked: {newBadges.map(b => `${b.emoji} ${b.title}`).join(' · ')}
            </strong>
          </div>
        )}

        <div className="row g-2">
          {badges.map(b => (
            <div className="col-6 col-sm-4 col-lg-3" key={b.id}>
              <div
                className="rounded-3 p-3 text-center h-100"
                title={b.earned ? (b.detail || b.title) : b.hint}
                style={b.earned
                  ? {background:'var(--purple-section)', border:'1.5px solid var(--border-purple2)'}
                  : {background:'var(--item-bg)', border:'1px solid var(--row-sep)', opacity:0.6}}
              >
                <div style={{fontSize:'28px', lineHeight:1.2, filter: b.earned ? 'none' : 'grayscale(1)'}}>
                  {b.earned ? b.emoji : '🔒'}
                </div>
                <div className="fw-bold mt-1" style={{color:'var(--text-main)', fontSize:'12px'}}>
                  {b.title}
                </div>
                {b.earned ? (
                  b.detail && <div style={{color:'var(--text-muted2)', fontSize:'10px', marginTop:'2px'}}>{b.detail}</div>
                ) : (
                  <>
                    <div style={{color:'var(--text-soft)', fontSize:'10px', marginTop:'2px', lineHeight:1.4}}>{b.hint}</div>
                    {b.progress && b.progress.now > 0 && (
                      <div className="mt-2">
                        <div style={{height:'4px', borderRadius:'2px', background:'var(--row-sep)', overflow:'hidden'}}>
                          <div style={{height:'100%', width:`${Math.round((b.progress.now / b.progress.of) * 100)}%`, background:'#6f42c1', borderRadius:'2px'}}></div>
                        </div>
                        <div style={{color:'var(--text-soft)', fontSize:'10px', marginTop:'2px'}}>{b.progress.now} / {b.progress.of}</div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

      </div>
    </>
  );
};

export default AchievementsCard;
